import { Button, Container, Typography, useMediaQuery } from '@ag1/ui';
import clsx from 'clsx';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { useStoreCurrency } from 'state/hooks/useStoreCurrency';
import LegalText from '../LegalText/LegalText';
import styles from './StickyCTA.module.scss';

type StickyCTAProps = {
  isVisible: boolean;
  subscriptionLabel?: string;
  oneTimeTitle?: string;
  price?: string;
  oneTimePrice?: string;
  quantity: number;
  isOtpProduct: boolean;
  showShopPay?: boolean;
  showStandardCheckout?: boolean;
  checkoutButtonDisabled: boolean;
  checkoutButtonLabel?: string;
  onClick?: (event: SyntheticEvent) => void;
  showOldPrice?: boolean;
  legalTextSuperScript?: boolean;
  legalText?: string;
};

const StickyCTA = ({
  isVisible,
  subscriptionLabel,
  oneTimeTitle,
  price,
  oneTimePrice,
  quantity,
  isOtpProduct,
  showShopPay,
  showStandardCheckout,
  checkoutButtonDisabled,
  checkoutButtonLabel,
  onClick,
  showOldPrice = false,
  legalTextSuperScript = false,
  legalText
}: StickyCTAProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { symbolCurrency } = useStoreCurrency();
  const atSmall = useMediaQuery('s');

  useEffect(() => {
    setIsOpen(isVisible);
  }, [isVisible]);

  // Scale prices based on quantity
  const isValidQty = Number.isInteger(quantity) && quantity > 0;
  const scaledOneTimePrice =
    isValidQty && oneTimePrice ? (parseFloat(oneTimePrice) * quantity).toString() : oneTimePrice;
  const scaledOldPrice =
    isValidQty && oneTimePrice ? (parseFloat(oneTimePrice) * quantity).toString() : oneTimePrice;

  const standardCheckoutIcon = 'arrowRight';

  const subscriptionTitle = subscriptionLabel;
  const title = isOtpProduct ? oneTimeTitle : subscriptionTitle;

  return (
    <div className={clsx(isOpen && styles.active, styles.root)}>
      <Container className={styles.container} narrow>
        <div>
          <Typography
            variant="subtitle"
            component="h2"
            size="1"
            weight="700"
            noPadding
            className={styles.productTitle}
            color="black-new">
            {title}
          </Typography>
          <div className={styles.priceWrapper}>
            <Typography
              variant="body"
              component="p"
              size="1"
              noPadding
              className={styles.price}
              color="black-new">
              {symbolCurrency}
              {isOtpProduct ? scaledOneTimePrice : price}
            </Typography>
            {showOldPrice && scaledOldPrice && !isOtpProduct && (
              <div className={styles.oldPriceWrapper}>
                <Typography
                  variant="body"
                  component="p"
                  size="1"
                  noPadding
                  className={styles.oldPrice}
                  color="grey-3-new">
                  {symbolCurrency}
                  {scaledOldPrice}
                </Typography>
                {legalTextSuperScript && <sup className={styles.priceSuper}>†</sup>}
              </div>
            )}
          </div>
          {!isOtpProduct && (
            <LegalText
              text={legalText || ''}
              showSuperScript={legalTextSuperScript}
              className={styles.legalText}
            />
          )}
        </div>
        <div className={styles.btnWrapper}>
          {showShopPay && (
            <Button
              className={styles.checkoutBtn}
              size={{ l: 'large', m: 'medium', s: 'small', xxs: 'small' }}
              rounded
              icon="shopPay"
              iconPosition="left">
              Shop Pay
            </Button>
          )}
          {showStandardCheckout && (
            <Button
              onClick={(e) => {
                setIsOpen(false);
                if (onClick) onClick(e);
              }}
              className={styles.checkoutBtn}
              disabled={checkoutButtonDisabled}
              size={{ l: 'large', m: 'medium', s: 'small', xxs: 'small' }}
              rounded
              icon={atSmall ? standardCheckoutIcon : undefined}
              iconPosition="right">
              {checkoutButtonLabel}
            </Button>
          )}
        </div>
      </Container>
    </div>
  );
};

export default StickyCTA;
